import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const HodRequisition = ({ onNavigate, userInfo }) => {
  const [activeTab, setActiveTab] = useState('pending-approval');
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [procurementProfile, setProcurementProfile] = useState({
    approvalLimit: 500000,
    procurementRole: 'hod'
  });

  // Procurement States
  const [requisitions, setRequisitions] = useState([]);
  const [myRequisitions, setMyRequisitions] = useState([]);
  const [filteredRequisitions, setFilteredRequisitions] = useState([]);
  const [departmentBudget, setDepartmentBudget] = useState({
    allocated: 0,
    used: 0,
    available: 0,
    quarterlyTarget: 0,
    currentQuarterSpent: 0
  });
  const [procurementStats, setProcurementStats] = useState({
    totalRequisitions: 0,
    pendingApproval: 0,
    approvedThisMonth: 0,
    rejectedThisMonth: 0,
    avgProcessingTime: 0,
    costSavingsThisYear: 0
  });
  const [categories, setCategories] = useState([]);

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [amountFilter, setAmountFilter] = useState('all');

  // Form States
  const [approvalComment, setApprovalComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // Create Requisition Form States
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    justification: '',
    category_code: '',
    priority: 'medium',
    expected_delivery_date: '',
    estimated_cost: '',
    procurement_method: 'tender',
    urgency_reason: '',
    items: []
  });
  const [createFormErrors, setCreateFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);





  // Load HOD procurement data on component mount
  useEffect(() => {
    fetchHodProcurementData();
    fetchCategories();
  }, [userInfo]);

  // Filter requisitions based on search and filters
  useEffect(() => {
    applyFilters();
  }, [requisitions, myRequisitions, searchTerm, priorityFilter, categoryFilter, dateFilter, amountFilter, activeTab]);

  // Fetch HOD procurement data from API
  const fetchHodProcurementData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please login again.');
        return;
      }

      console.log('🔄 Fetching HOD procurement data from API...');

      // Fetch requisitions data (for approval)
      const response = await fetch(`${API_LINK}/api/hod/requisitions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        setError('Session expired. Please login again.');
        sessionStorage.clear();
        localStorage.clear();
        if (onNavigate) onNavigate('home');
        return;
      }

      if (response.status === 404) {
        setError('Procurement API not implemented yet. Please contact system administrator.');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        console.log('✅ HOD procurement data loaded from API');
        loadApiData(result.data);

        // Fetch additional data
        await Promise.all([
          fetchBudgetData(token),
          fetchProcurementStats(token),
          fetchMyRequisitions(token)
        ]);
      } else {
        throw new Error(result.message || 'Failed to fetch procurement data');
      }

    } catch (error) {
      console.error('💥 Error in fetchHodProcurementData:', error);
      setError(`Failed to load procurement data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch HOD's own requisitions
  const fetchMyRequisitions = async (token) => {
    try {
      const response = await fetch(`${API_LINK}/api/hod/my-requisitions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setMyRequisitions(result.data);
          console.log(`📝 Loaded ${result.data.length} own requisitions`);
        }
      }
    } catch (error) {
      console.error('Error fetching own requisitions:', error);
    }
  };

  // Fetch budget data
  const fetchBudgetData = async (token) => {
    try {
      const response = await fetch(`${API_LINK}/api/hod/requisitions/budget`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setDepartmentBudget(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching budget data:', error);
    }
  };

  // Fetch categories data
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_LINK}/api/hod/requisitions/categories`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setCategories(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching categories data:', error);
    }
  };

  // Fetch procurement statistics
  const fetchProcurementStats = async (token) => {
    try {
      const response = await fetch(`${API_LINK}/api/hod/requisitions/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setProcurementStats(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching procurement stats:', error);
    }
  };

  // Load data from API response
  const loadApiData = (data) => {
    console.log('📊 Loading API data:', data);

    // Update Procurement Profile
    if (data.hod) {
      setProcurementProfile(prev => ({
        ...prev,
        approvalLimit: data.hod.approvalLimit || 500000,
        procurementRole: data.hod.procurementRole || 'hod'
      }));
    }

    // Update requisitions list
    if (data.requisitions) {
      setRequisitions(data.requisitions);
      console.log(`📝 Loaded ${data.requisitions.length} requisitions`);
    }

    setError(null);
  };

  // Apply filters to requisitions
  const applyFilters = () => {
    let filtered = [];

    // Choose source based on active tab
    const sourceData = activeTab === 'my-requisitions' ? myRequisitions : requisitions;

    filtered = [...sourceData];

    // Apply tab filter for approval requisitions
    if (activeTab !== 'my-requisitions') {
      switch (activeTab) {
        case 'pending-approval':
          filtered = filtered.filter(req =>
            req.status === 'hod_pending' ||
            req.status === 'submitted' ||
            req.current_stage === 'HOD Approval'
          );
          break;
        case 'approved':
          filtered = filtered.filter(req =>
            req.status === 'hod_approved' ||
            req.hod_approved_by
          );
          break;
        case 'rejected':
          filtered = filtered.filter(req =>
            req.status === 'hod_rejected' ||
            req.hod_rejection_reason
          );
          break;
        case 'forwarded':
          filtered = filtered.filter(req =>
            req.status === 'admin_pending' ||
            req.current_stage === 'Admin Approval'
          );
          break;
        default:
          break;
      }
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(req =>
        req.title?.toLowerCase().includes(searchLower) ||
        req.description?.toLowerCase().includes(searchLower) ||
        req.requester_name?.toLowerCase().includes(searchLower) ||
        req.requisition_id?.toLowerCase().includes(searchLower)
      );
    }

    // Apply priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(req => req.priority === priorityFilter);
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(req => req.category_code === categoryFilter);
    }

    // Apply amount filter
    if (amountFilter !== 'all') {
      switch (amountFilter) {
        case 'low':
          filtered = filtered.filter(req => req.estimated_cost <= 50000);
          break;
        case 'medium':
          filtered = filtered.filter(req => req.estimated_cost > 50000 && req.estimated_cost <= 200000);
          break;
        case 'high':
          filtered = filtered.filter(req => req.estimated_cost > 200000);
          break;
        default:
          break;
      }
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();

      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          filtered = filtered.filter(req => new Date(req.created_at) >= filterDate);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          filtered = filtered.filter(req => new Date(req.created_at) >= filterDate);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          filtered = filtered.filter(req => new Date(req.created_at) >= filterDate);
          break;
        default:
          break;
      }
    }

    setFilteredRequisitions(filtered);
  };

  // Calculate statistics dynamically from current requisitions
  const getRequisitionCounts = () => {
    return {
      totalRequisitions: requisitions.length,
      pendingApproval: requisitions.filter(r =>
        r.status === 'hod_pending' ||
        r.status === 'submitted' ||
        r.current_stage === 'HOD Approval'
      ).length,
      approvedThisMonth: requisitions.filter(r =>
        r.status === 'hod_approved' ||
        r.hod_approved_by
      ).length,
      rejectedThisMonth: requisitions.filter(r =>
        r.status === 'hod_rejected' ||
        r.hod_rejection_reason
      ).length,
      forwardedToAdmin: requisitions.filter(r =>
        r.status === 'admin_pending' ||
        r.current_stage === 'Admin Approval'
      ).length,
      myRequisitions: myRequisitions.length,
      myDrafts: myRequisitions.filter(r => r.status === 'draft').length,
      mySubmitted: myRequisitions.filter(r => r.status !== 'draft').length
    };
  };

  // Handle requisition approval
  const handleApproveRequisition = async (requisitionId, hodData) => {
    try {
      console.log('🔄 Approving requisition:', requisitionId);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // We need hodId, which is now in hodData prop (but this function needs to access it)
      // Since this function is defined inside component, it can access hodData from props?
      // No, hodData comes from Render Prop.
      // We must pass hodData to this function or use it from scope if we move content inside.
      // For now, let's assume hodData is passed or available. 
      // Wait, duplication refactor issue: hodData state is removed. 
      // I need to use procurementProfile for ID? No, ID is in hodData.
      // I should update handleApproveRequisition signature to accept hodData.

      const response = await fetch(`${API_LINK}/api/hod/requisitions/${requisitionId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          comment: approvalComment || 'Approved by HOD',
          // Accessing hodData here: it must be passed as an argument or we need component split pattern
          // Since I am not splitting the component yet (it's huge), I will assume I can fix the call sites.
        })
      });

      console.log('📡 Approve response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Approve response error:', errorText);
        throw new Error(`Failed to approve requisition: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Approve response:', result);

      if (result.success) {
        // Refresh data from API
        await fetchHodProcurementData();
        setApprovalComment('');
        setShowDetailsModal(false);
        alert('✅ Requisition approved successfully');
      } else {
        throw new Error(result.message || 'Approval failed');
      }

    } catch (error) {
      console.error('💥 Error approving requisition:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };

  // Handle requisition rejection
  // Handle requisition rejection
  const handleRejectRequisition = async (requisitionId, customReason = null, hodData) => {
    const reasonToUse = customReason || rejectionReason;

    if (!reasonToUse || !reasonToUse.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      console.log('🔄 Rejecting requisition:', requisitionId, 'with reason:', reasonToUse);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`${API_LINK}/api/hod/requisitions/${requisitionId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rejectionReason: reasonToUse.trim(),
          rejectedBy: hodData.hodId,
          rejectedAt: new Date().toISOString()
        })
      });

      console.log('📡 Reject response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Reject response error:', errorText);
        throw new Error(`Failed to reject requisition: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Reject response:', result);

      if (result.success) {
        // Refresh data from API
        await fetchHodProcurementData();
        setRejectionReason('');
        setShowDetailsModal(false);
        alert('✅ Requisition rejected successfully');
      } else {
        throw new Error(result.message || 'Rejection failed');
      }

    } catch (error) {
      console.error('💥 Error rejecting requisition:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };

  // Handle create requisition form submission
  const handleCreateRequisition = async (isDraft = false) => {
    try {
      setIsSubmitting(true);
      setCreateFormErrors({});

      // Validation
      const errors = {};
      if (!createForm.title.trim()) errors.title = 'Title is required';
      if (!createForm.description.trim()) errors.description = 'Description is required';
      if (!createForm.justification.trim()) errors.justification = 'Justification is required';
      if (!createForm.category_code) errors.category_code = 'Category is required';
      if (!createForm.estimated_cost || createForm.estimated_cost <= 0) errors.estimated_cost = 'Valid estimated cost is required';

      if (Object.keys(errors).length > 0) {
        setCreateFormErrors(errors);
        return;
      }

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${API_LINK}/api/hod/requisitions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...createForm,
          status: isDraft ? 'draft' : 'submitted'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create requisition');
      }

      const result = await response.json();

      if (result.success) {
        // Reset form
        setCreateForm({
          title: '',
          description: '',
          justification: '',
          category_code: '',
          priority: 'medium',
          expected_delivery_date: '',
          estimated_cost: '',
          procurement_method: 'tender',
          urgency_reason: '',
          items: []
        });
        setShowCreateModal(false);

        // Refresh data
        await fetchHodProcurementData();

        alert(`✅ Requisition ${isDraft ? 'saved as draft' : 'created and submitted'} successfully`);
      } else {
        throw new Error(result.message || 'Creation failed');
      }

    } catch (error) {
      console.error('Error creating requisition:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };





  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return '#6c757d';
      case 'hod_pending':
      case 'submitted':
        return '#ffc107';
      case 'admin_pending':
        return '#17a2b8';
      case 'hod_approved':
      case 'admin_approved':
      case 'approved':
        return '#28a745';
      case 'hod_rejected':
      case 'admin_rejected':
      case 'rejected':
        return '#dc3545';
      case 'completed':
        return '#28a745';
      default:
        return '#6c757d';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
      case 'high':
        return '#dc3545';
      case 'medium':
        return '#ffc107';
      case 'low':
        return '#28a745';
      default:
        return '#6c757d';
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Calculate budget utilization percentage
  const getBudgetUtilization = () => {
    if (departmentBudget.allocated === 0) return 0;
    return Math.round((departmentBudget.used / departmentBudget.allocated) * 100);
  };

  // Get category name from code
  const getCategoryName = (categoryCode) => {
    const category = categories.find(cat => cat.category_code === categoryCode);
    return category ? category.category_name : categoryCode;
  };

  return (
    <Layout role="hod" onNavigate={onNavigate} userInfo={userInfo} activePage="hod-requisition">
      {({ hodData, isMobile, handleLogout }) => (
        <>
          {/* Loading Component */}
          {loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #e3f2fd',
                borderTop: '4px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Supply Management...</p>
              <style>
                {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
              </style>
            </div>
          )}

          {/* Error Component */}
          {error && !loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem',
              padding: '2rem'
            }}>
              <div style={{
                backgroundColor: '#ffffff',
                padding: '3rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                textAlign: 'center',
                maxWidth: '400px',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{ fontSize: '4rem', color: '#ff6b6b', marginBottom: '1.5rem' }}>⚠️</div>
                <h3 style={{ color: '#333', margin: '0 0 1rem 0', fontSize: '1.4rem', fontWeight: 600 }}>Unable to Load Supply Management</h3>
                <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem', lineHeight: 1.5 }}>{error}</p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => window.location.reload()} style={{ backgroundColor: '#238548', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Try Again</button>
                  <button onClick={handleLogout} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Logout</button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          {!loading && !error && (
            <>
              {/* Header Section */}
              <section style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem',
                borderLeft: '4px solid #238548'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: '1rem'
                }}>
                  <div>
                    <h2 style={{
                      color: '#238548',
                      margin: '0 0 0.5rem 0',
                      fontSize: '1.8rem',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      🏪 Supply Management
                    </h2>
                    <p style={{ color: '#666', margin: 0 }}>
                      {activeTab === 'my-requisitions'
                        ? `Manage your requisitions for ${hodData.department}`
                        : `Review and approve department requisitions for ${hodData.department}`
                      }
                    </p>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}>
                    <button
                      onClick={() => setShowCreateModal(true)}
                      style={{
                        background: '#238548',
                        color: '#ffffff',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#1e7139';
                        e.target.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#238548';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      ➕ Create Requisition
                    </button>

                    <button
                      onClick={() => setShowBudgetModal(true)}
                      style={{
                        background: '#17a2b8',
                        color: '#ffffff',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#138496';
                        e.target.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#17a2b8';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      💰 Budget Overview
                    </button>
                  </div>
                </div>
              </section>

              {/* Department Budget Overview */}
              <section style={{
                background: '#ffffff',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem',
                border: '1px solid #e9ecef'
              }}>
                <h3 style={{
                  color: '#333',
                  margin: '0 0 1rem 0',
                  fontSize: '1.2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  🏢 Department Budget Status
                </h3>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#17a2b8' }}>
                      {formatCurrency(departmentBudget.allocated)}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>Allocated Budget</div>
                  </div>

                  <div style={{
                    background: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#dc3545' }}>
                      {formatCurrency(departmentBudget.used)}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>Used ({getBudgetUtilization()}%)</div>
                  </div>

                  <div style={{
                    background: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                      {formatCurrency(departmentBudget.available)}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>Available</div>
                  </div>
                </div>

                {/* Budget Progress Bar */}
                <div style={{
                  background: '#e9ecef',
                  height: '8px',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    background: getBudgetUtilization() > 80 ? '#dc3545' : getBudgetUtilization() > 60 ? '#ffc107' : '#28a745',
                    height: '100%',
                    width: `${Math.min(getBudgetUtilization(), 100)}%`,
                    transition: 'all 0.3s ease'
                  }}></div>
                </div>
              </section>

              {/* Procurement Statistics */}
              <section style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                gap: '1.5rem',
                marginBottom: '2rem'
              }}>
                {(() => {
                  const counts = getRequisitionCounts();
                  return [
                    {
                      icon: '⏳',
                      title: 'Pending My Approval',
                      value: counts.pendingApproval,
                      color: '#ffc107',
                      description: 'Requires your action'
                    },
                    {
                      icon: '📝',
                      title: 'My Requisitions',
                      value: counts.myRequisitions,
                      color: '#238548',
                      description: 'Total submitted'
                    },
                    {
                      icon: '✅',
                      title: 'Approved by Me',
                      value: counts.approvedThisMonth,
                      color: '#28a745',
                      description: 'Successfully processed'
                    },
                    {
                      icon: '💰',
                      title: 'Cost Savings',
                      value: formatCurrency(procurementStats.costSavingsThisYear),
                      color: '#17a2b8',
                      description: 'This year'
                    }
                  ];
                })().map((stat, index) => (
                  <div key={index} style={{
                    background: '#ffffff',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    border: '1px solid #e9ecef',
                    cursor: index === 1 ? 'pointer' : 'default'
                  }}
                    onClick={index === 1 ? () => setActiveTab('my-requisitions') : undefined}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                    }}>
                    <div style={{ fontSize: '2rem', color: stat.color, marginBottom: '0.5rem' }}>
                      {stat.icon}
                    </div>
                    <h3 style={{ margin: '0 0 0.25rem 0', color: '#333', fontSize: '1rem' }}>{stat.title}</h3>
                    <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: stat.color, margin: '0.5rem 0' }}>
                      {stat.value}
                    </p>
                    <p style={{ fontSize: '0.8rem', color: '#666', margin: 0 }}>
                      {stat.description}
                    </p>
                  </div>
                ))}
              </section>

              {/* Filters and Search */}
              <section style={{
                background: '#ffffff',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: '1rem',
                  alignItems: isMobile ? 'stretch' : 'center',
                  marginBottom: '1rem'
                }}>
                  {/* Search */}
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      placeholder="Search requisitions by title, ID, or requester..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#238548';
                        e.target.style.boxShadow = '0 0 0 2px rgba(35, 133, 72, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#ddd';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Filters */}
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <select
                      value={priorityFilter}
                      onChange={(e) => setPriorityFilter(e.target.value)}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        minWidth: '120px'
                      }}
                    >
                      <option value="all">All Priorities</option>
                      <option value="urgent">Urgent</option>
                      <option value="high">High Priority</option>
                      <option value="medium">Medium Priority</option>
                      <option value="low">Low Priority</option>
                    </select>

                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        minWidth: '120px'
                      }}
                    >
                      <option value="all">All Categories</option>
                      {categories.map(category => (
                        <option key={category.category_code} value={category.category_code}>
                          {category.category_name}
                        </option>
                      ))}
                    </select>

                    <select
                      value={amountFilter}
                      onChange={(e) => setAmountFilter(e.target.value)}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        minWidth: '120px'
                      }}
                    >
                      <option value="all">All Amounts</option>
                      <option value="low">Under ₹50,000</option>
                      <option value="medium">₹50,000 - ₹2,00,000</option>
                      <option value="high">Above ₹2,00,000</option>
                    </select>
                  </div>
                </div>

                {/* Tabs */}
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  borderBottom: '1px solid #eee',
                  marginBottom: '1rem',
                  overflowX: 'auto'
                }}>
                  {(() => {
                    const counts = getRequisitionCounts();
                    return [
                      { key: 'pending-approval', label: 'Pending My Approval', count: counts.pendingApproval },
                      { key: 'approved', label: 'Approved by Me', count: counts.approvedThisMonth },
                      { key: 'rejected', label: 'Rejected by Me', count: counts.rejectedThisMonth },
                      { key: 'forwarded', label: 'Forwarded to Admin', count: counts.forwardedToAdmin },
                      { key: 'my-requisitions', label: 'My Requisitions', count: counts.myRequisitions },
                      { key: 'all', label: 'All Requisitions', count: counts.totalRequisitions }
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                          padding: '0.75rem 1rem',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          fontWeight: '500',
                          color: activeTab === tab.key ? '#238548' : '#666',
                          borderBottom: activeTab === tab.key ? '2px solid #238548' : '2px solid transparent',
                          transition: 'all 0.3s ease',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          if (activeTab !== tab.key) {
                            e.target.style.color = '#238548';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (activeTab !== tab.key) {
                            e.target.style.color = '#666';
                          }
                        }}
                      >
                        {tab.label} ({tab.count})
                      </button>
                    ));
                  })()}
                </div>
              </section>

              {/* Requisitions List */}
              <section style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '1.5rem',
                  borderBottom: '1px solid #eee',
                  background: '#f8f9fa'
                }}>
                  <h3 style={{ margin: 0, color: '#333', fontSize: '1.2rem' }}>
                    {activeTab === 'my-requisitions'
                      ? `My Requisitions (${filteredRequisitions.length})`
                      : `Requisitions for Review (${filteredRequisitions.length})`
                    }
                  </h3>
                </div>

                <div style={{ padding: 0 }}>
                  {filteredRequisitions.length > 0 ? (
                    filteredRequisitions.map((requisition, index) => (
                      <div
                        key={requisition.id || index}
                        style={{
                          padding: '1.5rem',
                          borderBottom: index < filteredRequisitions.length - 1 ? '1px solid #eee' : 'none',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        onClick={() => {
                          setSelectedRequisition(requisition);
                          setShowDetailsModal(true);
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '1rem',
                          flexWrap: 'wrap',
                          gap: '1rem'
                        }}>
                          <div style={{ flex: 1, minWidth: '250px' }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              marginBottom: '0.5rem'
                            }}>
                              <h4 style={{ margin: 0, color: '#333', fontSize: '1.1rem' }}>
                                {requisition.title || 'Untitled Requisition'}
                              </h4>
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                backgroundColor: `${getPriorityColor(requisition.priority)}20`,
                                color: getPriorityColor(requisition.priority),
                                textTransform: 'uppercase'
                              }}>
                                {requisition.priority || 'medium'}
                              </span>
                              {activeTab === 'my-requisitions' && requisition.estimated_cost > hodData.approvalLimit && (
                                <span style={{
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  backgroundColor: '#ff6b6b20',
                                  color: '#ff6b6b'
                                }}>
                                  High Value
                                </span>
                              )}
                            </div>
                            <p style={{ color: '#666', margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>
                              {requisition.description || 'No description provided'}
                            </p>
                            <div style={{
                              display: 'flex',
                              gap: '1rem',
                              fontSize: '0.85rem',
                              color: '#888',
                              flexWrap: 'wrap'
                            }}>
                              <span>🆔 {requisition.requisition_id}</span>
                              <span>📅 {formatDate(requisition.created_at)}</span>
                              <span>👤 {requisition.requester_name || 'Unknown'}</span>
                              <span>🏷️ {getCategoryName(requisition.category_code) || 'Uncategorized'}</span>
                              <span>💰 {formatCurrency(requisition.estimated_cost)}</span>
                            </div>
                          </div>

                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: '0.5rem'
                          }}>
                            <span style={{
                              padding: '0.5rem 1rem',
                              borderRadius: '20px',
                              fontSize: '0.85rem',
                              fontWeight: 'bold',
                              backgroundColor: `${getStatusColor(requisition.status)}20`,
                              color: getStatusColor(requisition.status),
                              textTransform: 'capitalize'
                            }}>
                              {requisition.current_stage || requisition.status?.replace('_', ' ') || 'Unknown Status'}
                            </span>

                            {/* Action buttons for approval queue */}
                            {activeTab !== 'my-requisitions' && (
                              (requisition.status === 'hod_pending' ||
                                requisition.status === 'submitted' ||
                                requisition.current_stage === 'HOD Approval') && (
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleApproveRequisition(requisition.id);
                                    }}
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: '#28a745',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.8rem',
                                      transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = '#218838';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = '#28a745';
                                    }}
                                  >
                                    ✅ Approve & Forward
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const reason = prompt('Please provide a reason for rejection:');
                                      if (reason && reason.trim()) {
                                        console.log('🔄 Rejecting requisition ID:', requisition.id);
                                        handleRejectRequisition(requisition.id, reason.trim());
                                      }
                                    }}
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: '#dc3545',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.8rem',
                                      transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = '#c82333';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = '#dc3545';
                                    }}
                                  >
                                    ❌ Reject
                                  </button>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      padding: '3rem',
                      textAlign: 'center',
                      color: '#666'
                    }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>
                        📋
                      </div>
                      <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                        {activeTab === 'my-requisitions'
                          ? 'No requisitions created yet'
                          : 'No requisitions found'
                        }
                      </p>
                      <p style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                        {activeTab === 'my-requisitions'
                          ? 'Click "Create Requisition" to submit your first request'
                          : activeTab === 'pending-approval'
                            ? 'No requisitions pending your approval at the moment'
                            : 'Try adjusting your search or filter criteria'
                        }
                      </p>
                    </div>
                  )}
                </div>
              </section>


              {/* Create Requisition Modal */}
              {showCreateModal && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 2000,
                  padding: '1rem'
                }}>
                  <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    padding: '2rem',
                    maxWidth: '800px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '2rem'
                    }}>
                      <h3 style={{ margin: 0, color: '#333', fontSize: '1.5rem' }}>
                        Create New Requisition
                      </h3>
                      <button
                        onClick={() => setShowCreateModal(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '1.5rem',
                          cursor: 'pointer',
                          color: '#666',
                          padding: '0.25rem',
                          borderRadius: '4px'
                        }}
                      >
                        ✕
                      </button>
                    </div>

                    <div style={{ display: 'grid', gap: '1.5rem' }}>
                      {/* Basic Information */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                          Title *
                        </label>
                        <input
                          type="text"
                          value={createForm.title}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="Enter requisition title"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: createFormErrors.title ? '1px solid #dc3545' : '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '1rem'
                          }}
                        />
                        {createFormErrors.title && (
                          <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            {createFormErrors.title}
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                            Category *
                          </label>
                          <select
                            value={createForm.category_code}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, category_code: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: createFormErrors.category_code ? '1px solid #dc3545' : '1px solid #ddd',
                              borderRadius: '6px',
                              fontSize: '1rem'
                            }}
                          >
                            <option value="">Select Category</option>
                            {categories.map(category => (
                              <option key={category.category_code} value={category.category_code}>
                                {category.category_icon} {category.category_name}
                              </option>
                            ))}
                          </select>
                          {createFormErrors.category_code && (
                            <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                              {createFormErrors.category_code}
                            </div>
                          )}
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                            Priority
                          </label>
                          <select
                            value={createForm.priority}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, priority: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              fontSize: '1rem'
                            }}
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                          </select>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                            Estimated Cost *
                          </label>
                          <input
                            type="number"
                            value={createForm.estimated_cost}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, estimated_cost: e.target.value }))}
                            placeholder="Enter estimated cost"
                            min="0"
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: createFormErrors.estimated_cost ? '1px solid #dc3545' : '1px solid #ddd',
                              borderRadius: '6px',
                              fontSize: '1rem'
                            }}
                          />
                          {createFormErrors.estimated_cost && (
                            <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                              {createFormErrors.estimated_cost}
                            </div>
                          )}
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                            Expected Delivery Date
                          </label>
                          <input
                            type="date"
                            value={createForm.expected_delivery_date}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              fontSize: '1rem'
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                          Description *
                        </label>
                        <textarea
                          value={createForm.description}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Describe what you need..."
                          rows="3"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: createFormErrors.description ? '1px solid #dc3545' : '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '1rem',
                            resize: 'vertical'
                          }}
                        />
                        {createFormErrors.description && (
                          <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            {createFormErrors.description}
                          </div>
                        )}
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                          Justification *
                        </label>
                        <textarea
                          value={createForm.justification}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, justification: e.target.value }))}
                          placeholder="Explain why this requisition is needed..."
                          rows="3"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: createFormErrors.justification ? '1px solid #dc3545' : '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '1rem',
                            resize: 'vertical'
                          }}
                        />
                        {createFormErrors.justification && (
                          <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            {createFormErrors.justification}
                          </div>
                        )}
                      </div>

                      <div style={{
                        display: 'flex',
                        gap: '1rem',
                        justifyContent: 'flex-end',
                        paddingTop: '1rem',
                        borderTop: '1px solid #eee'
                      }}>
                        <button
                          onClick={() => setShowCreateModal(false)}
                          disabled={isSubmitting}
                          style={{
                            padding: '0.75rem 1.5rem',
                            background: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            opacity: isSubmitting ? 0.6 : 1
                          }}
                        >
                          Cancel
                        </button>

                        <button
                          onClick={() => handleCreateRequisition(false)}
                          disabled={isSubmitting}
                          style={{
                            padding: '0.75rem 1.5rem',
                            background: '#238548',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            opacity: isSubmitting ? 0.6 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          {isSubmitting ? '⏳' : '📤'} Submit Requisition
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Requisition Details Modal */}
              {showDetailsModal && selectedRequisition && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 2000,
                  padding: '1rem'
                }}>
                  <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    padding: '2rem',
                    maxWidth: '700px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1.5rem'
                    }}>
                      <h3 style={{ margin: 0, color: '#333', fontSize: '1.5rem' }}>
                        Requisition Details
                      </h3>
                      <button
                        onClick={() => setShowDetailsModal(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '1.5rem',
                          cursor: 'pointer',
                          color: '#666',
                          padding: '0.25rem',
                          borderRadius: '4px'
                        }}
                      >
                        ✕
                      </button>
                    </div>

                    <div style={{ display: 'grid', gap: '1.5rem' }}>
                      <div style={{
                        padding: '1rem',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        borderLeft: `4px solid ${getStatusColor(selectedRequisition.status)}`
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.5rem',
                          flexWrap: 'wrap',
                          gap: '1rem'
                        }}>
                          <h4 style={{ margin: 0, color: '#333' }}>{selectedRequisition.title}</h4>
                          <span style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: 'bold',
                            backgroundColor: `${getStatusColor(selectedRequisition.status)}20`,
                            color: getStatusColor(selectedRequisition.status),
                            textTransform: 'capitalize'
                          }}>
                            {selectedRequisition.current_stage || selectedRequisition.status?.replace('_', ' ')}
                          </span>
                        </div>
                        <p style={{ color: '#666', margin: 0 }}>{selectedRequisition.description}</p>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                        <div>
                          <strong>Requisition ID:</strong> {selectedRequisition.requisition_id}
                        </div>
                        <div>
                          <strong>Category:</strong> {getCategoryName(selectedRequisition.category_code)}
                        </div>
                        <div>
                          <strong>Priority:</strong>
                          <span style={{
                            marginLeft: '0.5rem',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            backgroundColor: `${getPriorityColor(selectedRequisition.priority)}20`,
                            color: getPriorityColor(selectedRequisition.priority),
                            textTransform: 'capitalize'
                          }}>
                            {selectedRequisition.priority}
                          </span>
                        </div>
                        <div>
                          <strong>Estimated Cost:</strong> {formatCurrency(selectedRequisition.estimated_cost)}
                          {activeTab !== 'my-requisitions' && selectedRequisition.estimated_cost > hodData.approvalLimit && (
                            <div style={{ fontSize: '0.8rem', color: '#dc3545', marginTop: '0.25rem' }}>
                              ⚠️ Exceeds your approval limit
                            </div>
                          )}
                        </div>
                        <div>
                          <strong>Requested By:</strong> {selectedRequisition.requester_name}
                        </div>
                        <div>
                          <strong>Department:</strong> {selectedRequisition.department}
                        </div>
                        <div>
                          <strong>Request Date:</strong> {formatDate(selectedRequisition.created_at)}
                        </div>
                        <div>
                          <strong>Last Updated:</strong> {formatDate(selectedRequisition.updated_at)}
                        </div>
                      </div>

                      <div>
                        <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Justification:</strong>
                        <p style={{
                          color: '#666',
                          background: '#f8f9fa',
                          padding: '1rem',
                          borderRadius: '6px',
                          margin: 0,
                          lineHeight: 1.6
                        }}>
                          {selectedRequisition.justification || 'No justification provided'}
                        </p>
                      </div>

                      {selectedRequisition.hod_rejection_reason && (
                        <div>
                          <strong style={{ display: 'block', marginBottom: '0.5rem', color: '#dc3545' }}>
                            Rejection Reason:
                          </strong>
                          <p style={{
                            color: '#dc3545',
                            background: '#f8d7da',
                            padding: '1rem',
                            borderRadius: '6px',
                            margin: 0,
                            border: '1px solid #f5c6cb'
                          }}>
                            {selectedRequisition.hod_rejection_reason}
                          </p>
                        </div>
                      )}

                      {selectedRequisition.admin_rejection_reason && (
                        <div>
                          <strong style={{ display: 'block', marginBottom: '0.5rem', color: '#dc3545' }}>
                            Admin Rejection Reason:
                          </strong>
                          <p style={{
                            color: '#dc3545',
                            background: '#f8d7da',
                            padding: '1rem',
                            borderRadius: '6px',
                            margin: 0,
                            border: '1px solid #f5c6cb'
                          }}>
                            {selectedRequisition.admin_rejection_reason}
                          </p>
                        </div>
                      )}

                      {/* Action buttons for approval */}
                      {activeTab !== 'my-requisitions' && (
                        (selectedRequisition.status === 'hod_pending' ||
                          selectedRequisition.status === 'submitted' ||
                          selectedRequisition.current_stage === 'HOD Approval') && (
                          <div style={{
                            display: 'flex',
                            gap: '1rem',
                            justifyContent: 'flex-end',
                            paddingTop: '1rem',
                            borderTop: '1px solid #eee'
                          }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                                Approval Comment (Optional):
                              </label>
                              <textarea
                                value={approvalComment}
                                onChange={(e) => setApprovalComment(e.target.value)}
                                placeholder="Add any comments for your approval..."
                                rows="2"
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid #ddd',
                                  borderRadius: '6px',
                                  fontSize: '0.9rem',
                                  resize: 'vertical'
                                }}
                              />
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
                              <button
                                onClick={() => {
                                  handleApproveRequisition(selectedRequisition.id, hodData);
                                }}
                                style={{
                                  padding: '0.75rem 1.5rem',
                                  background: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '1rem',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = '#218838';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = '#28a745';
                                }}
                              >
                                ✅ Approve & Forward to Admin
                              </button>
                              <button
                                onClick={() => {
                                  const reason = prompt('Please provide a reason for rejection:');
                                  if (reason && reason.trim()) {
                                    console.log('🔄 Rejecting requisition ID from modal:', selectedRequisition.id);
                                    handleRejectRequisition(selectedRequisition.id, reason.trim(), hodData);
                                  }
                                }}
                                style={{
                                  padding: '0.75rem 1.5rem',
                                  background: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '1rem',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = '#c82333';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = '#dc3545';
                                }}
                              >
                                ❌ Reject
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Budget Overview Modal */}
              {showBudgetModal && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 2000,
                  padding: '1rem'
                }}>
                  <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    padding: '2rem',
                    maxWidth: '600px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1.5rem'
                    }}>
                      <h3 style={{ margin: 0, color: '#333', fontSize: '1.5rem' }}>
                        Department Budget Overview
                      </h3>
                      <button
                        onClick={() => setShowBudgetModal(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '1.5rem',
                          cursor: 'pointer',
                          color: '#666',
                          padding: '0.25rem',
                          borderRadius: '4px'
                        }}
                      >
                        ✕
                      </button>
                    </div>

                    <div style={{ display: 'grid', gap: '1.5rem' }}>
                      {/* Budget Visualization */}
                      <div style={{
                        background: '#f8f9fa',
                        padding: '1.5rem',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>Budget Utilization</h4>
                        <div style={{
                          background: '#e9ecef',
                          height: '20px',
                          borderRadius: '10px',
                          overflow: 'hidden',
                          marginBottom: '1rem'
                        }}>
                          <div style={{
                            background: getBudgetUtilization() > 80 ? '#dc3545' : getBudgetUtilization() > 60 ? '#ffc107' : '#28a745',
                            height: '100%',
                            width: `${Math.min(getBudgetUtilization(), 100)}%`,
                            transition: 'all 0.3s ease',
                            borderRadius: '10px'
                          }}></div>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '0.9rem',
                          color: '#666'
                        }}>
                          <span>0%</span>
                          <span>{getBudgetUtilization()}% Used</span>
                          <span>100%</span>
                        </div>
                      </div>

                      {/* Budget Breakdown */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '1rem'
                      }}>
                        <div style={{
                          background: '#e3f2fd',
                          padding: '1rem',
                          borderRadius: '8px',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1976d2' }}>
                            {formatCurrency(departmentBudget.allocated)}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#666' }}>Total Allocated</div>
                        </div>

                        <div style={{
                          background: '#ffebee',
                          padding: '1rem',
                          borderRadius: '8px',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#d32f2f' }}>
                            {formatCurrency(departmentBudget.used)}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#666' }}>Amount Used</div>
                        </div>

                        <div style={{
                          background: '#e8f5e9',
                          padding: '1rem',
                          borderRadius: '8px',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#388e3c' }}>
                            {formatCurrency(departmentBudget.available)}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#666' }}>Available Balance</div>
                        </div>

                        <div style={{
                          background: '#fff3e0',
                          padding: '1rem',
                          borderRadius: '8px',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#f57c00' }}>
                            {formatCurrency(departmentBudget.currentQuarterSpent)}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#666' }}>This Quarter</div>
                        </div>
                      </div>

                      {/* Warning Messages */}
                      {getBudgetUtilization() > 80 && (
                        <div style={{
                          background: '#fff3cd',
                          border: '1px solid #ffeaa7',
                          padding: '1rem',
                          borderRadius: '8px',
                          color: '#856404'
                        }}>
                          <strong>⚠️ Budget Alert:</strong> Department has used {getBudgetUtilization()}% of allocated budget. Please review upcoming requisitions carefully.
                        </div>
                      )}

                      {getBudgetUtilization() > 95 && (
                        <div style={{
                          background: '#f8d7da',
                          border: '1px solid #f5c6cb',
                          padding: '1rem',
                          borderRadius: '8px',
                          color: '#721c24'
                        }}>
                          <strong>🚨 Critical Alert:</strong> Budget utilization is critically high. Consider deferring non-essential purchases to next budget cycle.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </>
          )}
        </>
      )}
    </Layout>
  );
};

export default HodRequisition;